import React, { useState, useEffect } from 'react';
import { cmsApi } from '../../../lib/api/cms';
import TranslationEditor from '../TranslationEditor';
import ImageUpload from '../ImageUpload';

const HeroEditor: React.FC = () => {
  const [heroData, setHeroData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHeroData();
  }, []);

  const loadHeroData = async () => {
    setLoading(true);
    try {
      const { data, error } = await cmsApi.getSection('hero');
      if (error && error.code !== 'PGRST116') { // Not found error
        setError('Andmete laadimine ebaõnnestus');
        return;
      }
      setHeroData(data || {});
    } catch (err) {
      setError('Andmete laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (imageUrl: string | null) => {
    try {
      const { error } = await cmsApi.upsertSection({
        type: 'hero',
        background_image: imageUrl,
        ...heroData
      });

      if (error) {
        setError('Pildi salvestamine ebaõnnestus');
        return;
      }

      setHeroData((prev: any) => ({ ...prev, background_image: imageUrl }));
      
      // Trigger website update to refresh hero background on frontend
      window.dispatchEvent(new CustomEvent('translationsUpdated'));

      // Show success message
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      successDiv.textContent = 'Taustapilt edukalt salvestatud!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (err) {
      setError('Pildi salvestamine ebaõnnestus');
    }
  };

  const translationFields = [
    {
      key: 'hero.title',
      label: 'Pealkiri',
      type: 'text' as const,
      placeholder: 'Metsamajanduse eksperdid alates 2005'
    },
    {
      key: 'hero.subtitle',
      label: 'Alampealkiri',
      type: 'textarea' as const,
      placeholder: 'Kvaliteetsed teenused metsamaa majandamisel, raieõiguse ning metsamaa ostust ja müügist.'
    },
    {
      key: 'hero.servicesButton',
      label: 'Teenuste nupu tekst',
      type: 'text' as const,
      placeholder: 'Meie teenused'
    },
    {
      key: 'hero.contactButton',
      label: 'Kontakti nupu tekst',
      type: 'text' as const,
      placeholder: 'Võta ühendust'
    },
    {
      key: 'hero.scrollText',
      label: 'Kerimise tekst',
      type: 'text' as const,
      placeholder: 'Loe rohkem'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Avalehe haldus</h1>
        <p className="mt-2 text-gray-600">
          Halda avalehe sisu, pealkirju ja taustapilti. Muudatused kajastuvad kohe veebilehel.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Avalehe sisu</h3>
          <p className="text-sm text-gray-500 mt-1">
            Need tekstid kuvatakse avalehe hero sektsioonis
          </p>
        </div>
        <div className="p-6 space-y-8">
          {/* Text Content */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Tekstisisu</h4>
            <TranslationEditor
              section="hero"
              fields={translationFields}
              onSave={() => {
                // Refresh page content if needed
                window.dispatchEvent(new CustomEvent('translationsUpdated'));
              }}
            />
          </div>

          {/* Background Image */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Taustapilt</h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-700">
                💡 <strong>Oluline:</strong> Siin üles laetud pilt kuvatakse automaatselt veebilehe avalehe taustana. 
                Muudatused kajastuvad kohe pärast salvestamist.
              </p>
            </div>
            <ImageUpload
              currentImage={heroData?.background_image}
              onImageChange={handleImageChange}
              folder="hero"
              label="Avalehe taustapilt"
            />
            <p className="text-sm text-gray-500 mt-2">
              Soovitatud suurus: 1920x1080px või suurem. Maksimaalne failisuurus: 5MB.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroEditor;